import { GameState, Player, Morale } from '../../../domain/entities';

/**
 * MoraleProcessor - Handles player morale changes
 *
 * Responsibilities:
 * - Update morale based on match results
 * - Factor in individual player ratings
 * - Apply random morale fluctuations
 * - Track significant morale changes
 */
export class MoraleProcessor {
    private static readonly moraleMap: Record<Morale, number> = {
        'Ecstatic': 5,
        'Happy': 4,
        'Content': 3,
        'Unhappy': 2,
        'Furious': 1
    };

    private static readonly numberToMorale: Record<number, Morale> = {
        5: 'Ecstatic',
        4: 'Happy',
        3: 'Content',
        2: 'Unhappy',
        1: 'Furious'
    };

    /**
     * Process morale changes for a team after a match
     */
    static processMoraleChanges(
        team: any,
        isWin: boolean,
        isDraw: boolean,
        startingXI: number[],
        allPlayersById: Map<number, Player>,
        playerRatings: { [playerId: number]: number },
        gameState: GameState,
        moraleCounters: { boosts: number, drops: number }
    ): void {
        startingXI.forEach(playerId => {
            const player = allPlayersById.get(playerId);
            if (!player || player.id < 0) return; // Skip emergency youth players

            const oldMorale = player.morale;

            // Calculate morale change
            let moraleChange = this.calculateMoraleChange(isWin, isDraw, playerRatings[playerId]);

            // Apply morale change
            if (moraleChange !== 0) {
                player.morale = this.changeMorale(player.morale, moraleChange);
                player.moraleReason = moraleChange > 0
                    ? 'Good recent performance'
                    : 'Poor recent performance';
            }

            // Track significant changes for player team
            if (player.morale !== oldMorale && team.name === gameState.playerTeamName) {
                this.trackSignificantChange(oldMorale, player.morale, moraleCounters, player);
            }
        });
    }

    /**
     * Calculate morale change based on match result and individual performance
     */
    private static calculateMoraleChange(
        isWin: boolean,
        isDraw: boolean,
        rating: number
    ): number {
        let moraleChange = 0;

        // Base change from result
        if (isWin) {
            moraleChange += 1;
        } else if (!isDraw) {
            moraleChange -= 1;
        }

        // Modify based on individual performance
        if (rating > 8) {
            moraleChange++; // Excellent performance
        }
        if (rating < 5.5) {
            moraleChange--; // Poor performance
        }

        // Random fluctuation (20% chance)
        if (Math.random() < 0.2) {
            moraleChange += Math.random() < 0.5 ? 1 : -1;
        }

        return moraleChange;
    }

    /**
     * Change morale level, clamped between Furious (1) and Ecstatic (5)
     */
    private static changeMorale(currentMorale: Morale, change: number): Morale {
        const currentLevel = this.moraleMap[currentMorale];
        const newLevel = Math.max(1, Math.min(5, currentLevel + change));
        return this.numberToMorale[newLevel];
    }

    /**
     * Track significant morale changes for notifications
     */
    private static trackSignificantChange(
        oldMorale: Morale,
        newMorale: Morale,
        moraleCounters: { boosts: number, drops: number },
        player: Player
    ): void {
        const oldLevel = this.moraleMap[oldMorale];
        const newLevel = this.moraleMap[newMorale];

        // Significant boost: crossed into Happy/Ecstatic territory
        if (newLevel > oldLevel && newLevel >= 4 && oldLevel < 4) {
            moraleCounters.boosts++;
        }
        // Significant drop: crossed into Unhappy/Furious territory
        else if (newLevel < oldLevel && newLevel <= 2 && oldLevel > 2) {
            // Only count if due to poor performance with 3+ consecutive bad games
            if (player.moraleReason === 'Poor recent performance' &&
                (player.consecutiveBadFormGames || 0) >= 3) {
                moraleCounters.drops++;
            }
        }
    }
}
